import { Box, Button, Typography } from '@mui/material';
import { FC, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Colors, Key } from '@/assets';
import { Alert, InputText, Loading, PrimaryButton, showToatify } from '@/components';
import { AuthActions, AuthThunks, CreateTenantForm, TenantInfo } from '@/features/auth';
import { CreateTenantFormSchema } from '@/features/auth/schema';
import { classValidatorFormResolverFactory } from '@/helper';
import { useMessageData, useIsMountedRef } from '@/hooks';
import { Screens } from '@/routes';

const CreateTenantFormSchemaValidatorResolver = classValidatorFormResolverFactory<CreateTenantFormSchema>(CreateTenantFormSchema);

interface IProps {
    notSignOut?: boolean;
    submitSuccess?: () => void;
    tenantEditing?: TenantInfo;
}
export const TenantForm: FC<IProps> = ({ notSignOut, submitSuccess, tenantEditing }) => {
    const navigate = useNavigate();
    const { isSuccess, message, setMessage, clearMessage } = useMessageData();
    const dispatch = useDispatch();
    const isMountedRef = useIsMountedRef();

    const [loading, setLoading] = useState<boolean>(false);

    const form = useForm<CreateTenantFormSchema>({
        resolver: CreateTenantFormSchemaValidatorResolver,
        defaultValues: {
            name: tenantEditing?.name
        }
    });

    const onSubmit = async (data: CreateTenantForm) => {
        try {
            setLoading(true);
            if (tenantEditing) {
                await dispatch(AuthThunks.updateTenant(tenantEditing.id, data));
                showToatify('Workspace updated successfully');
            } else {
                await dispatch(AuthThunks.createTenant(data));
                showToatify('Workspace created successfully');
            }
            if (submitSuccess) {
                submitSuccess();
                return;
            }
            navigate(Screens.Dashboard);
        } catch (error: any) {
            setMessage({ message: error.message });
        } finally {
            if (isMountedRef.current) return;
            setLoading(false);
        }
    };

    const logout = () => {
        dispatch(AuthActions.logout());
    };

    return (
        <FormProvider {...form}>
            <Loading loading={loading} />
            <Key />
            <Typography variant="h1" sx={{ mt: 3, fontWeight: 600, color: Colors.tangaroaBlue }}>
                {tenantEditing ? 'Edit your Workspace' : 'Create new Workspace'}
            </Typography>
            {!tenantEditing && (
                <Typography variant="h6" sx={{ mt: 2, fontWeight: 'normal', color: Colors.lynchBlue }}>
                    A space for multiple purposes
                </Typography>
            )}
            <form style={{ width: '100%' }} onSubmit={form.handleSubmit(onSubmit)}>
                <Box sx={{ mt: 4 }}>
                    <Alert isSuccess={isSuccess} message={message} clearMessage={clearMessage} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <InputText name="name" label="Workspace Name" />
                        <PrimaryButton title={tenantEditing ? 'Save' : 'Create Workspace'} submit />
                        {!notSignOut && (
                            <Button onClick={logout} sx={{ color: Colors.lynchBlue, py: 1.5, boxShadow: 'none' }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 500, color: Colors.lynchBlue, ml: 0.5 }}>
                                    Sign Out
                                </Typography>
                            </Button>
                        )}
                    </Box>
                </Box>
            </form>
        </FormProvider>
    );
};
